import React, { useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "@material-ui/styles";
import { ValidatorForm } from "react-material-ui-form-validator";
import { Card, ClickAwayListener } from "@material-ui/core";
import {
	FormActions,
	FormButton,
	FormSubmitButton,
	Dialog,
	PopupSnackbar,
	SearchDropdown,
	StyledModal,
} from "frame/components";
import { SiteForm } from "features/forms";
import moment from "moment";
import { TimesheetForm } from "./timesheet-form";
import { safetyWiseApi } from "utils/safetyWiseApi";
import { defaultState } from "../timesheet-form-reducer";
import { useDispatch, useSelector } from "react-redux";
import { isNotNullOrEmpty } from "utils";
import { onList as onListLeaveTypes } from "../../leave-type/actions";
import {
	TIMESHEET_OVERLAP_WARNING_MESSAGE,
	checkTimesheetOverlap,
	timesheetFormRangeToUtcIso,
} from "../timesheet-overlap-check";

const useStyles = makeStyles((theme) => ({
	root: {
		flex: 1,
		flexDirection: "row",
		padding: theme.spacing(3, 3, 10, 3)
	},
	actions: {
		marginTop: theme.spacing(3),
		paddingBottom: theme.spacing(8)
	},
	menu: {
		"padding": theme.spacing(3, 0, 0, 0),
		"& > *": {
			marginRight: theme.spacing(3)
		}
	}
}));

export const EditTimesheetForm = ({
	id,
	onClose,
	open,
	timesheetList,
	currentOrganisationGUID,
	isLeaveRecord = false
}) => {
	const classes = useStyles();
	const [timesheetState, setTimesheetState] = useState(defaultState);
	const [saveAndCancelDisabled, setSaveAndCancelDisabled] = useState(false);
	const [snackMessage, setSnackMessage] = useState(null);
	const [overlapDialogOpen, setOverlapDialogOpen] = useState(false);
	const [overlapMessage, setOverlapMessage] = useState(TIMESHEET_OVERLAP_WARNING_MESSAGE);
	const pendingSaveBodyRef = useRef(null);
	const dispatch = useDispatch();
	const leaveTypes = useSelector(({ leaveTypes }) => leaveTypes?.data || []);
	const leaveTypesLoading = useSelector(({ leaveTypes }) => !!leaveTypes?.isLoadingList);
	const hasNetwork = useSelector(({ queue }) => !!queue.hasNetwork);

	const leaveTypeOptions = useMemo(() => {
		const selectedId = timesheetState?.siteId ? String(timesheetState.siteId) : "";
		return (isNotNullOrEmpty(leaveTypes) ? leaveTypes : [])
			// In edit flows we allow selecting inactive leave types if they were previously used.
			.filter((lt) => lt && (lt.active || String(lt.id) === selectedId))
			.map((lt) => ({ label: lt.leaveType, value: String(lt.id) }))
			.sort((a, b) => (a.label > b.label ? 1 : -1));
	}, [leaveTypes, timesheetState?.siteId]);

	// Ensure leave types are loaded when editing leave
	useEffect(() => {
		if (!open || !isLeaveRecord) return;
		dispatch(onListLeaveTypes());
	}, [open, isLeaveRecord, dispatch]);

	const computeValidity = useMemo(() => {
		return (nextState) => {
			const next = nextState || defaultState;
			const t = next.timesheet || {};

			const hasRequired =
				!!(next.siteId && t.staffId && t.startDate && t.endDate && t.startTime && t.endTime);

			const start =
				t.startDate && t.startTime
					? moment(`${t.startDate} ${t.startTime}`, "YYYY-MM-DD HH:mm")
					: null;
			const end =
				t.endDate && t.endTime
					? moment(`${t.endDate} ${t.endTime}`, "YYYY-MM-DD HH:mm")
					: null;

			const isEndDateTimeInvalid = !!(start && end && end.isBefore(start));

			return {
				...next,
				isEndDateTimeInvalid,
				isValid: hasRequired && !isEndDateTimeInvalid,
			};
		};
	}, []);

	const safeSetTimesheetState = (nextState) => {
		setTimesheetState(computeValidity(nextState));
	};

	useEffect(() => {
		if (!open) {
			return;
		}

		const timesheet = timesheetList && timesheetList.filter((t) => t.id === id)[0];

		if (timesheet) {
			const resolvedLeaveTypeId =
				timesheet.leaveTypeID || timesheet.leaveTypeId || timesheet.siteId;
			const resolvedLeaveTypeIdStr =
				resolvedLeaveTypeId !== null && resolvedLeaveTypeId !== undefined
					? String(resolvedLeaveTypeId)
					: "";
			safeSetTimesheetState({
				siteId: isLeaveRecord ? resolvedLeaveTypeIdStr : timesheet.siteId,
			// Work Type / Equipment are selected in SiteForm (root-level), not inside timesheet.*
			organisationTaskTypeId: timesheet.organisationTaskTypeId || "",
			equipment: timesheet.equipment || [],
				timesheet: {
					...timesheet,
					startDate: moment.utc(timesheet.startDateTimeUTC).local().format("YYYY-MM-DD"),
					startTime: moment.utc(timesheet.startDateTimeUTC).local().format("HH:mm"),
					lunchTime: timesheet.lunchTimeMinutes ? timesheet.lunchTimeMinutes.toString() : "0",
					endDate: timesheet.endDateTimeUTC
						? moment.utc(timesheet.endDateTimeUTC).local().format("YYYY-MM-DD")
						: "",
					endTime: timesheet.endDateTimeUTC ? moment.utc(timesheet.endDateTimeUTC).local().format("HH:mm") : ""
				},
			});
		}
	}, [open, id, timesheetList, computeValidity, isLeaveRecord]);

	// If the leave record doesn't include a leaveTypeId, fall back to matching by name once leave types are loaded.
	useEffect(() => {
		if (!open || !isLeaveRecord) return;
		if (!isNotNullOrEmpty(leaveTypes)) return;
		if (timesheetState?.siteId) return;

		const savedName = (timesheetState?.timesheet?.leaveType || "").toString().trim();
		if (!savedName) return;

		const match = leaveTypes.find(
			(lt) => (lt?.leaveType || "").toString().trim().toLowerCase() === savedName.toLowerCase()
		);
		if (match?.id) {
			safeSetTimesheetState({ ...timesheetState, siteId: String(match.id) });
		}
	}, [open, isLeaveRecord, leaveTypes, timesheetState]);

	useEffect(() => {
		timesheetState.success && onClose();
	}, [timesheetState.success]);

	const saveTimesheetUpdate = (body) => {
		safetyWiseApi
			.post(currentOrganisationGUID + "/timesheet/update/" + timesheetState.timesheet.id, body)
			.then(() => {
				setSaveAndCancelDisabled(false);
				setSnackMessage({ message: "Saved.", severity: "success" });
				onClose();
			})
			.catch((error) => {
				setSaveAndCancelDisabled(false);

				const status = error?.response?.status;
				const serverMessage =
					error?.response?.data?.message ||
					error?.response?.data?.error ||
					(typeof error?.response?.data === "string" ? error.response.data : null);

				const message =
					status === 401
						? "Your session has expired. Please log in again and retry."
						: serverMessage || error?.message || "Failed to save changes. Please try again.";

				setSnackMessage({ message, severity: "error" });
			});
	};

	const onSubmit = async (event) => {
		setSaveAndCancelDisabled(true);
		event.preventDefault();

		const t = timesheetState.timesheet || {};
		const hasRequired =
			!!(timesheetState.siteId && t.staffId && t.startDate && t.endDate && t.startTime && t.endTime);

		const start = hasRequired
			? moment(`${t.startDate} ${t.startTime}`, "YYYY-MM-DD HH:mm")
			: null;
		const end = hasRequired
			? moment(`${t.endDate} ${t.endTime}`, "YYYY-MM-DD HH:mm")
			: null;

		if (!hasRequired) {
			setSaveAndCancelDisabled(false);
			setSnackMessage({ message: "Please complete all required fields before saving.", severity: "error" });
			return;
		}

		if (start && end && end.isBefore(start)) {
			setSaveAndCancelDisabled(false);
			safeSetTimesheetState({ ...timesheetState, isEndDateTimeInvalid: true });
			setSnackMessage({ message: "End date/time must be later than start.", severity: "error" });
			return;
		}

		const body = {
			siteId: timesheetState.siteId,
			staffId: timesheetState.timesheet.staffId,
			startDateTimeUTC: moment(
				timesheetState.timesheet.startDate + " " + timesheetState.timesheet.startTime,
				"YYYY-MM-DD HH:mm"
			).toISOString(),
			endDateTimeUTC: moment(
				timesheetState.timesheet.endDate + " " + timesheetState.timesheet.endTime,
				"YYYY-MM-DD HH:mm"
			).toISOString(),
			lunchTimeMinutes: parseInt(timesheetState.timesheet.lunchTime, 10),
			notes: timesheetState.timesheet.notes,
			startLatitude: timesheetState.timesheet.startLatitude,
			startLongitude: timesheetState.timesheet.startLongitude,
			endLatitude: timesheetState.timesheet.endLatitude,
			endLongitude: timesheetState.timesheet.endLongitude,
		};

		if (isLeaveRecord) {
			// Leaves use a Leave Type (not a Site). The API uses leaveTypeID for create; include it for update too.
			body.leaveTypeID = timesheetState.siteId;
			saveTimesheetUpdate(body);
			return;
		}

		// Work Type / Equipment are selected in SiteForm (root-level). Keep fallback for older state shapes.
		body.organisationTaskTypeId =
			(timesheetState.organisationTaskTypeId ?? timesheetState.timesheet.organisationTaskTypeId) || null;
		body.equipment =
			timesheetState.equipment || timesheetState.timesheet.equipment || [];

		const range = timesheetFormRangeToUtcIso(timesheetState.timesheet, moment);
		const result = await checkTimesheetOverlap({
			organisationId: currentOrganisationGUID,
			staffId: body.staffId,
			startDateTimeUTC: range?.startDateTimeUTC || body.startDateTimeUTC,
			endDateTimeUTC: range?.endDateTimeUTC || body.endDateTimeUTC,
			excludeTimesheetId: timesheetState.timesheet.id,
			hasNetwork,
		});

		if (result.hasOverlap) {
			setSaveAndCancelDisabled(false);
			pendingSaveBodyRef.current = body;
			setOverlapMessage(result.message || TIMESHEET_OVERLAP_WARNING_MESSAGE);
			setOverlapDialogOpen(true);
			return;
		}

		saveTimesheetUpdate(body);
	};

	const onConfirmOverlapSave = () => {
		const body = pendingSaveBodyRef.current;
		pendingSaveBodyRef.current = null;
		setOverlapDialogOpen(false);
		if (!body) return;
		setSaveAndCancelDisabled(true);
		saveTimesheetUpdate(body);
	};

	// StyledModal calls onClose after onConfirm; only unlock when Cancel left a pending save.
	const onCloseOverlapDialog = () => {
		const hadPending = pendingSaveBodyRef.current != null;
		pendingSaveBodyRef.current = null;
		setOverlapDialogOpen(false);
		if (hadPending) {
			setSaveAndCancelDisabled(false);
		}
	};

	// Determine the title based on whether this is a leave record
	const modalTitle = isLeaveRecord ? "Edit Leave" : "Edit Timesheet";

	return (
		<>
			<ClickAwayListener
				onClickAway={() => {
					if (overlapDialogOpen) return;
					onClose();
				}}
			>
				<>
					<Dialog open={open} aria-labelledby="Timesheets" title={modalTitle} onClose={onClose}>
						<ValidatorForm autoComplete="off" noValidate onSubmit={onSubmit}>
							<div>
								{isLeaveRecord ? (
									<Card className={classes.root}>
										<div style={{ padding: 16 }}>
											{leaveTypesLoading || !isNotNullOrEmpty(leaveTypes) ? (
												<p>Loading Leave Types ...</p>
											) : (
												<SearchDropdown
													id="leaveType"
													data-cy="leaveType"
													label="Leave Type *"
													disabled={false}
													defaultOptions={leaveTypeOptions}
													value={timesheetState.siteId || ""}
													onChange={(value) =>
														safeSetTimesheetState({ ...timesheetState, siteId: value })
													}
												/>
											)}
										</div>
									</Card>
								) : (
									<SiteForm
										values={timesheetState}
										onChange={safeSetTimesheetState}
										hideDate
										// Show when feature flags enable them; SiteForm handles flag-based hiding.
										hideTaskTypes={false}
										hideEquipment={false}
										allowEquipmentUsage={true}
										isAdmin={true}
									/>
								)}
								{(isLeaveRecord ? true : !!timesheetState.siteId) && (
									<TimesheetForm
										values={timesheetState}
										onChange={safeSetTimesheetState}
										fromEditTimesheetForm
										leaveForm={isLeaveRecord}
									/>
								)}
							</div>
							<FormActions className={classes.actions}>
								<FormSubmitButton
									disabled={timesheetState.isEndDateTimeInvalid || saveAndCancelDisabled}
									loading={timesheetState.isLoading}
								>
									Save
								</FormSubmitButton>
								<FormButton
									disabled={timesheetState.isLoading || saveAndCancelDisabled}
									onClick={onClose}
								>
									Cancel
								</FormButton>
							</FormActions>
						</ValidatorForm>
					</Dialog>
				</>
			</ClickAwayListener>
			<StyledModal
				open={overlapDialogOpen}
				title="Overlapping time"
				onClose={onCloseOverlapDialog}
				onConfirm={onConfirmOverlapSave}
				onConfirmText="Save anyway"
			>
				{overlapMessage}
			</StyledModal>
			{snackMessage?.message && (
				<PopupSnackbar
					message={snackMessage.message}
					coloured
					success={snackMessage.severity === "success"}
					error={snackMessage.severity === "error"}
					onExited={() => setSnackMessage(null)}
				/>
			)}
		</>
	);
};
